import {
  Brand,
  Cookie as CookieNames,
  FeatureFlag,
  License,
  ResolverTag
} from '@ps/web-core-enums';
import { Size as ModalSize } from '@ps/web-core-store';
import classNames from 'clsx';
import { get, isEmpty, omit, pickBy } from 'lodash';
import Cookies from 'universal-cookie';

import { PostMessage } from '@swf/core/enums';
import { getState, select } from '@swf/core/store';
import type {
  HandleMessageProps,
  ResolverProps,
  ResolverQuery
} from '@swf/core/types/AccountResolver';
import { BonusTags, Department, RollType } from '@swf/core/types/AccountResolver';
import { MiniGame } from '@swf/core/types/MiniGame';

const cookies = new Cookies();

const iframeModalStyle = (styles?: { iframeModal: string }) => ({
  className: styles?.iframeModal,
  size: ModalSize.EXTRA_LARGE
});

export const MINIGAME_TAGS = {
  [MiniGame.TheDeal1]: 'play-the-deal-webui',
  [MiniGame.TheDeal2]: 'play-the-deal-webui',
  [MiniGame.TheDeal3]: 'play-the-deal-webui',
  [MiniGame.SingleReel]: 'singlereel_game',
  [MiniGame.PickObject]: 'pickobject_game',
  [MiniGame.BonusWheel]: 'bonuswheel_game',
  [MiniGame.MatchDayMillions]: 'matchdaymillions_game'
};

export const getBonusPath = (rollType: RollType) => {
  switch (rollType) {
    case RollType.InstantBonuses:
      return 'instant-bonuses';
    case RollType.FreeSpinBonuses:
      return 'free-spin-bonuses';
    case RollType.GameTickets:
      return 'game-tickets';
  }
};

export const getAccountResolverProps = ({
  assetUrl,
  brand,
  brandName,
  close,
  country,
  emailValidated,
  embedded,
  embeddedRoute,
  firstSession,
  getTokenAndSignature,
  isPlayMoney,
  isRealMoneyOK,
  language,
  license,
  locationState,
  migrationRequired,
  migrationTarget,
  navigate,
  params,
  pathname,
  platform,
  push,
  setPendingNotifications,
  site,
  styles,
  isReloader,
  features,
  generatePath
}: ResolverProps) => {
  const pmcPaywall = features[FeatureFlag.PmcPaywall];
  const ice2Feature = features[FeatureFlag.Ice2Feature];
  const isPRHubEnabled = features[FeatureFlag.PRHub];
  const hspSwcDeposit = features[FeatureFlag.SwcDeposit];
  const isSWCWithdraw = features[FeatureFlag.SwcWithdrawal];
  const isSWCCashierHome = features[FeatureFlag.SwcCashier];
  const isSWCP2pTransfer = features[FeatureFlag.SwcP2pTransfer];
  const hspSwcDepositReloader = features[FeatureFlag.SwcDepositReloader];
  const isSWCManageCurrencies = features[FeatureFlag.SwcManageCurrencies];
  const disableNewUkAgeVerificationFlow = features[FeatureFlag.DisableNewUkAgeVerificationFlow];
  const isResolverRewriteForWC2Enabled = features[FeatureFlag.ResolverRewriteForWC2];
  const isSWCDeposit =
    (hspSwcDeposit && !isReloader) || (hspSwcDeposit && isReloader && hspSwcDepositReloader);
  const domain = select.location.getCookieDomain(getState());
  const port = select.location.getPort(getState());
  const pathBrandName = select.cms.getPathBrandName(getState());

  switch (true) {
    case !!(pathname.startsWith('/account/cashier') && params.department): {
      const { department } = params;
      const isPaywall = department === Department.Paywall;
      const isRealMoneyForm = department === Department.RMF;
      const query: ResolverQuery = { applePaySupported: false };
      const mitIdData =
        isRealMoneyForm && !isEmpty(omit(locationState, 'temporaryPassword'))
          ? locationState
          : undefined;

      const tag = {
        [Department.Balances]: 'wc2fe_balance',
        [Department.BankID]: 'open_bankid_validation',
        [Department.ConvertCurrencies]: 'wc2fe_convert_currencies',
        [Department.PreferredCurrency]: 'wc2fe_preferred_currency',
        [Department.Deposit]: 'wc2fe_deposit',
        [Department.History]: 'wc2fe_history',
        [Department.Home]: 'wc2fe_home',
        [Department.Paywall]: pmcPaywall ? 'pmc_paywall' : 'freemium-buy-play-chips',
        [Department.PlayMoney]: 'wc2fe_playmoney',
        [Department.P2pTransfer]: 'wc2fe_transfer',
        [Department.RMF]: 'web-real-money-form',
        [Department.Withdraw]: 'wc2fe_cashout'
      }[department];

      if (
        !isPlayMoney &&
        !emailValidated &&
        // @ts-ignore department is valid
        ![Department.Deposit, Department.Home].includes(department) &&
        !isRealMoneyForm
      ) {
        close?.('/account/cashier');
        setPendingNotifications();
      } else if (migrationRequired && department !== Department.Withdraw) {
        push?.('/account/migration');
      }

      if (isRealMoneyForm && !disableNewUkAgeVerificationFlow) {
        query.opencontext = firstSession ? 'registration' : 'login';
      } else if (isPaywall) {
        query.ram = 1; // to show close button
      }

      if (Department.Deposit === department && isSWCDeposit) {
        close?.('/account');
        navigate('/deposit');
      } else if (Department.History === department && isSWCCashierHome) {
        close?.('/account');
        navigate('/transaction-history');
      } else if (
        [Department.Home, Department.Balances, Department.PlayMoney].includes(department) &&
        isSWCCashierHome
      ) {
        close?.('/account');
        navigate(generatePath?.() || '');
      } else if (Department.Withdraw === department && isSWCWithdraw) {
        close?.('/account');
        navigate('/withdraw');
      } else if (
        department &&
        [Department.ConvertCurrencies, Department.PreferredCurrency].includes(department) &&
        isSWCManageCurrencies
      ) {
        close?.('/account');
        navigate('/manage-currencies');
      } else if (Department.P2pTransfer === department && isSWCP2pTransfer) {
        close?.('/account');
        navigate('/p2p-transfer');
      }

      try {
        query.applePaySupported = !!ApplePaySession && ApplePaySession.canMakePayments();
      } catch (e) {
        // ignore
      }

      const host = isPaywall ? 'playchips' : 'cashier';

      return {
        resolverProps: {
          tag,
          query: { ...query, brand: pathBrandName },
          mitIdData,
          host: isResolverRewriteForWC2Enabled ? `${host}.${domain}` : undefined, // TODO: remove hsp when PII is ready
          port,
          className: isPaywall ? (embeddedRoute ? styles?.iframeFullWindow : '') : styles?.overlay
        },
        styles: isPaywall
          ? {
              size: ModalSize.MEDIUM,
              className: classNames(styles?.iframeModal, pmcPaywall && styles?.paywall),
              hidden: false
            }
          : { hidden: true }
      };
    }
    case !!(
      (pathname.startsWith('/account/ice') || pathname.startsWith('/prhub/ice')) &&
      params.icegame
    ): {
      const { icegame, icegameId, trigger } = params;
      const iceParam = icegame === 'leaderboards' ? 'leaderboardid' : 'promoid';
      const hostingApp = pathname.startsWith('/prhub/ice') && isPRHubEnabled ? 'prhub' : undefined;
      const query = { brandName, site, [iceParam]: icegameId, trigger, hostingApp, platform };
      const challengesStyles =
        pathname.indexOf('/prhub/ice') > -1 && isPRHubEnabled ? null : iframeModalStyle(styles);

      return {
        resolverProps: {
          tag:
            icegame === 'leaderboards'
              ? 'ice-leaderboards'
              : ice2Feature
              ? ResolverTag.Ice2Home
              : ResolverTag.IceHome,
          query,
          scrolling: 'yes'
        },
        styles: challengesStyles
      };
    }
    case pathname.startsWith('/account/my-stars/rewards-store') ||
      pathname.startsWith('/prhub/rewards-store'): {
      const embeddedClassName = !isPRHubEnabled ? styles?.iframeFullWindow : '';
      const hostingApp =
        pathname.startsWith('/prhub/rewards-store') && isPRHubEnabled ? 'prhub' : undefined;
      return {
        resolverProps: {
          tag: 'mc_vipstore',
          query: { hostingApp },
          className: embeddedRoute ? embeddedClassName : ''
        },
        styles: !isPRHubEnabled ? iframeModalStyle(styles) : null
      };
    }
    case pathname.startsWith('/account/migration'): {
      if (embedded) {
        window?.starsweb?.app.execute('migrate');
        close?.('/account');
        break;
      }
      const { token, signature } = getTokenAndSignature();
      const query = {
        type: 'web',
        gver: 'WEB',
        country,
        ram: 1,
        token,
        signature,
        language,
        site,
        product: brand === Brand.PokerStarsCasino ? 'PokerStarsCasino' : brandName,
        devIx: cookies.get(CookieNames.INSTALL_ID),
        license,
        migrationTarget: migrationTarget || -1
      };

      return {
        resolverProps: {
          tag: 'web-migration-form',
          query,
          className: styles?.overlay,
          scrolling: 'auto'
        },
        styles: { hidden: true }
      };
    }
    case pathname.startsWith('/account/the-deal'): {
      if (!isRealMoneyOK) {
        push?.('/account/cashier/rmf');
        break;
      } else {
        const query = { ram: 1, isWeb: true, type: 'web', brand: brandName, site };

        return {
          resolverProps: { tag: 'play-the-deal-webui', query },
          styles: iframeModalStyle(styles)
        };
      }
    }
    case pathname.startsWith('/account/bonus-game'): {
      const tag =
        MINIGAME_TAGS[locationState?.minigameType as keyof typeof MINIGAME_TAGS] ||
        MINIGAME_TAGS[MiniGame.SingleReel];
      const query = {
        ...(locationState || {}),
        ram: 1,
        isWeb: true,
        type: 'web',
        brand: brandName,
        site
      };

      return {
        resolverProps: { tag, query },
        styles: iframeModalStyle(styles)
      };
    }
    case pathname.startsWith('/account/open-chest'): {
      const { chest } = locationState || {};
      const base = assetUrl.includes('.html') ? undefined : assetUrl;
      const { token, signature } = getTokenAndSignature();

      return {
        resolverProps: {
          tag: 'starsrewards_chest2',
          query: pickBy({ signature, token, chest, base })
        },
        styles: iframeModalStyle(styles)
      };
    }
    case !!(
      (pathname.startsWith('/account/my-stars/bonus') ||
        pathname.startsWith('/prhub/my-rewards/bonus')) &&
      params.bonusType
    ): {
      const { bonusType, bonusId } = params;
      const hostingApp =
        pathname.startsWith('/prhub/my-rewards/bonus') && isPRHubEnabled ? 'prhub' : undefined;

      return {
        resolverProps: {
          tag: BonusTags[bonusType as keyof typeof BonusTags],
          query: pickBy({ bonusid: bonusId, brand, hostingApp, platform }),
          className: styles?.inlineIframe,
          scrolling: 'yes',
          style: isPRHubEnabled ? { height: '100%', display: 'block' } : null
        },
        styles: null
      };
    }
    default: {
      return {
        resolverProps: null,
        styles: null
      };
    }
  }
};

let isRmfCompleted = false;
export const handleResolverPostMessage = async ({
  data,
  depositLimitPath,
  modalReplace,
  push,
  logout,
  close,
  dataIntegrityCheckFailed,
  refreshAccount,
  quickAccountCreationRequired,
  license,
  c2lHandler,
  setMitIdWc2Data
}: HandleMessageProps) => {
  if (typeof data === 'object') {
    if (
      data.command === PostMessage.OPEN_ACCOUNT &&
      data.arguments[0] === 'root.responsible.restrictDepositLimit'
    ) {
      modalReplace([{ path: depositLimitPath }]);
      return;
    }
    const { msgId, ...state } = data;
    switch (msgId) {
      case PostMessage.OPEN_MINIGAME:
        push({ pathname: '/account/bonus-game', state });
        return;
      case PostMessage.C2L: {
        c2lHandler(data.data.c2l);
        return;
      }
      case PostMessage.OPEN_MITID: {
        setMitIdWc2Data(state);
        return;
      }
      case PostMessage.OPEN_BONUSMANAGER: {
        const path = getBonusPath(data.rollType);
        path && modalReplace([{ path: `/account/my-stars/bonus/${path}` }]);
        return;
      }
    }

    const redirect = get(data, ['ps_openwebsite', 'webURL']);
    if (redirect) {
      window.location.href = redirect;
      return;
    }
  } else {
    const messageId: string = data.startsWith('{') ? JSON.parse(data).msgId : data;

    switch (messageId) {
      case PostMessage.CLOSE_MINIGAME:
        close?.('/account/bonus-game');
        close?.('/account/the-deal');
        return;
      case PostMessage.CLOSE_DEPOSIT: {
        if (dataIntegrityCheckFailed && !isRmfCompleted) {
          await logout({});
        }

        close?.('/account');
        return;
      }
      case PostMessage.CLOSE_COMPLETED_RMF: {
        isRmfCompleted = true;
        await refreshAccount();
        return;
      }
      case PostMessage.CLOSE_ICE:
      case PostMessage.CLOSE_VIPSTORE:
      case PostMessage.CLOSE_SRWRDCHESTS:
      case PostMessage.CLOSE_PMCASHIER: {
        close?.('/account');
        return;
      }
      case PostMessage.CLOSE_LICENSE_MIGRATION: {
        if (quickAccountCreationRequired) {
          await logout({});
        }

        close?.('/account');
        return;
      }
      case PostMessage.LICENSE_MIGRATION: {
        push('/account/migration');
        return;
      }
      case PostMessage.LOGOUT: {
        await logout({});
        return;
      }
      case PostMessage.AGE_VERIFICATION_COMPLETED: {
        license === License.UK
          ? push?.('/account/cashier/deposit')
          : push('/auth/registration/welcome');
        return;
      }
      case PostMessage.ACCOUNT_RMF: {
        push('/account/cashier/rmf');
        return;
      }
      case PostMessage.OPEN_DEPOSIT:
        push('/account/cashier/deposit');
        return;
      case PostMessage.OPEN_VERIFICATION:
        push('/auth/docverify');
        return;
      case PostMessage.OPEN_PAY_WALL:
        push('/account/cashier/paywall');
        return;
      case PostMessage.VERIFY_EMAIL:
        push({
          pathname: '/notifications/confirm-email',
          state: { sendEmail: true }
        });
        return;
    }
  }
};
